import React, { useEffect, useMemo, useState } from 'react'
import MuiDialogContent from '@mui/material/DialogContent'
import { useTypedSelector } from '../../reducers'
import moment from 'moment'
import {
  deleteAPIAccess,
  deleteAPIKeys,
  loadAPIAccessList
} from '../../actions/apiAccess'
import { useDispatch } from 'react-redux'
import {
  Input,
  InputAdornment,
  Typography,
  Box,
  Tooltip,
  Dialog,
  DialogTitle,
  IconButton as MuiIconButton
} from '@mui/material'
import Close from '@mui/icons-material/Close'
import Key from '@mui/icons-material/Key'
import { styled } from '@mui/material/styles'
import SearchInput from '../SearchInput'
import { defineMessages, useIntl } from 'react-intl'
import RefreshButton from '../buttons/RefreshButton'
import DeleteButton from '../buttons/DeleteButton'
import DataTable from '../DataTable'
import UsersNav from '../UsersNav'
import IconButton from '../buttons/IconButton'
import APIAccessForm from '../APIAccessForm'
import APIKeyForm from '../APIKeyForm'
import MikeSlidingPanel from '../SlidingPanel'
import IAPIAccess from '../../model/IAPIAccess'
import IAPIKey from '../../model/IAPIKey'
import { createAPIAccess, updateAPIAccess } from '../../actions/apiAccess'
import { createAPIKey, updateAPIKey } from '../../actions/apiAccess'
import { StateEnum } from '../../model/StateEnum'
import LongName from '../DataTable/renders/LongName'
import { formatUtcDateTime } from '../../utils/dateFormat'
import Renders from '../DataTable/renders'
import messages from '../../shared/messages'
import { IApiAccessGroup, sortApiAccessRows } from './sortApiAccessRows'
import { ReactComponent as Duplicate } from '@mike/mike-shared-frontend/media/icons/Duplicate'
import Button from '@mui/material/Button'
import copy from 'clipboard-copy'
import { css } from '@emotion/css'
import ConfirmationDialog from '../Dialog/ConfirmationDialog'
import { setLastAddedAPIKey } from '../../reducers/apiKeys'
import { useTablePageParam } from '../../hooks/use-table-page-param.hook'
import { useMeasuredHeight } from '../../hooks/use-measured-height.hook'
import { ReactComponent as Alert } from '@mike/mike-shared-frontend/media/icons/Alert'

const localMessages = defineMessages({
  title: {
    id: 'screens.apiAccess.title'
  },
  confirmationDialogTitle: {
    id: 'screens.apiAccess.confirmationDialog.title'
  },
  confirmationDialogMessage: {
    id: 'screens.apiAccess.confirmationDialog.message'
  },
  searchPlaceholder: {
    id: 'screens.apiAccess.input.search.placeholder'
  },
  createApiKey: {
    id: 'screens.apiAccess.button.createApiKey'
  },
  create: {
    id: 'screens.apiAccess.button.create'
  },
  key: {
    id: 'screens.apiAccess.apiKey'
  },
  expirationDate: {
    id: 'screens.apiAccess.expirationDate'
  },
  keyGeneratedTitle: {
    id: 'screens.apiAccess.keyGenerated.title'
  },
  keyGeneratedMessage: {
    id: 'screens.apiAccess.keyGenerated.message'
  },
  // Folder-scoped only: the tenant-wide /site-access/api screen lists the
  // tenant's own API accounts, not a folder's.
  folderDescription: {
    id: 'projectAccess.nav.api.description'
  }
})

const hoverStyle = () => {
  return css`
    &:hover {
      cursor: pointer;
    }
  `
}

const StyledTableTopHeader = styled('div')(({ theme }) => ({
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(2),
  backgroundColor: '#f2f5f7'
}))

const StyledTableTopActionButtons = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(2)
}))

const StyledLeftActionButtons = styled('div')({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'flex-start'
})

const StyledRightActionButtons = styled('div')({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'flex-end'
})

const StyledExpirationRow = styled('div')({
  display: 'flex'
})

const StyledDialogContent = styled(MuiDialogContent)(({ theme }) => ({
  maxWidth: 378,
  paddingBottom: theme.spacing(3),
  paddingRight: theme.spacing(3),
  backgroundColor: theme.palette.background.paper
}))

const APIAccess = (props: {
  projectId?: string
  hideNav?: boolean
  formsInDialog?: boolean
  pageParamName?: string
  disableRowClick?: boolean
  [key: string]: any
}) => {
  const {
    projectId: projectIdProp,
    hideNav,
    formsInDialog,
    pageParamName,
    disableRowClick
  } = props
  const tenantId = useTypedSelector(state => state.auth?.user?.tenantId) || ''
  const effectiveId = projectIdProp || tenantId
  const apiAccessList = useTypedSelector(state => state.apiAccess.apiAccessList)
  const apiKeyList = useTypedSelector(state => state.apiKeys.apiKeyList)
  const latestAddedApiKey = useTypedSelector(
    state => state.apiKeys.lastAddedApiKey
  )
  const loadingApiAccess =
    useTypedSelector(state => state.apiAccess.state) === StateEnum.LOADING
  const dispatch = useDispatch()
  const intl = useIntl()
  const [searchText, setSearchText] = useState<string>('')
  const [openAPIAccessEditor, setOpenAPIAccessEditor] = useState(false)
  const [selectedRows, setSelectedRows] = useState<(IAPIAccess & IAPIKey)[]>([])
  const [sortOrder, setSortOrder] = useState<string[]>(['asc'])
  const [sortOrderBy, setSortOrderBy] = useState<string[]>(['name'])
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState<boolean>(
    false
  )
  const [itemsToBeDeleted, setItemsToBeDeleted] = useState<
    (IAPIAccess & IAPIKey)[]
  >([])
  const [currentEditingApiAccess, setCurrentEditingApiAccess] = useState<
    IAPIAccess
  >({
    name: '',
    role: ''
  })
  const [openKeyEditor, setOpenKeyEditor] = useState(false)
  const [currentEditingKey, setCurrentEditingKey] = useState<IAPIKey>({
    name: ''
  })
  const [page, setPage] = useTablePageParam(pageParamName)
  const [rowsPerPage, setRowsPerPage] = useState<number>(
    () =>
      parseInt(localStorage.getItem('api-access-rowsPerPage') ?? '10', 10) || 10
  )
  // Only offer the pointer cursor where the row itself is a click target.
  const rowClassName = disableRowClick ? undefined : hoverStyle

  const projectNameRender = (value, item: IAPIAccess & IAPIKey) => {
    if (item && item.key) {
      return <LongName longName={value} />
    }

    return <LongName longName={value} variant={'bold'} />
  }

  const projectKeyRender = (value, item: IAPIAccess & IAPIKey) => {
    if (item && item.key) {
      return <LongName longName={value} />
    }

    return (
      <Tooltip title={intl.formatMessage(localMessages.createApiKey)}>
        <Button
          size="small"
          color="secondary"
          variant="outlined"
          onClick={event => {
            event.stopPropagation()
            setOpenKeyEditor(true)
            setCurrentEditingApiAccess(item)
            setCurrentEditingKey({ name: '' })
          }}
          startIcon={<Key />}
        >
          {intl.formatMessage(localMessages.create)}
        </Button>
      </Tooltip>
    )
  }

  const projectExpirationDateRender = (value, item: IAPIAccess & IAPIKey) => {
    if (item && item.key) {
      const expiry = moment.utc(value)
      const soonDate = moment.utc().add(7, 'days')

      return (
        <StyledExpirationRow>
          <LongName longName={formatUtcDateTime(value)} />
          {expiry.isBefore(soonDate) && <Alert />}
        </StyledExpirationRow>
      )
    }
  }

  const roleRender = (value, item: IAPIAccess & IAPIKey) => {
    if (!item?.['createdBy']) return ''

    if (!value || value === 'undefined') return <LongName longName={'None'} />

    return <LongName longName={value} />
  }

  useEffect(() => {
    dispatch(loadAPIAccessList(effectiveId))
  }, [dispatch, effectiveId])

  const [, measuredRef] = useMeasuredHeight()

  const onRefresh = () => {
    dispatch(loadAPIAccessList(effectiveId))
  }

  const onDelete = item => {
    setItemsToBeDeleted([item])
    setOpenDeleteConfirmation(true)
  }

  const onDeleteItems = () => {
    setItemsToBeDeleted(selectedRows)
    setOpenDeleteConfirmation(true)
  }

  const onColumnClick = (column: IAPIAccess & IAPIKey) => {
    if (column && column.key) {
      const apiAccess = apiAccessList.find(
        item => item.accountId === column.accountId
      )
      apiAccess && setCurrentEditingApiAccess(apiAccess)
      setCurrentEditingKey(column)
      setOpenKeyEditor(true)
    } else {
      setCurrentEditingApiAccess(column)
      setOpenAPIAccessEditor(true)
    }
  }

  const onHandleRequestSort = (orderBy: string, order: string) => {
    setSortOrder([order])
    setSortOrderBy([orderBy])
    setPage(0)
  }

  const sorterdAPIAccesList: (IAPIAccess | IAPIKey)[] = useMemo(() => {
    const groups: IApiAccessGroup[] = []

    if (searchText?.length === 0) {
      apiAccessList.forEach(apiAccess => {
        groups.push({
          account: apiAccess,
          keys: apiAccess.accountId ? apiKeyList[apiAccess.accountId] || [] : []
        })
      })
    } else {
      const lowerSearch = searchText.toLowerCase()

      apiAccessList.forEach(apiAccess => {
        if (apiAccess.accountId === undefined) return

        if (apiAccess.name.toLowerCase().includes(lowerSearch)) {
          groups.push({
            account: apiAccess,
            keys: apiKeyList[apiAccess.accountId] || []
          })
          return
        }

        const filteredKeys = apiKeyList[apiAccess.accountId]?.filter(
          key =>
            key.name.toLowerCase().includes(lowerSearch) ||
            key.key.toLowerCase().includes(lowerSearch)
        )

        if (filteredKeys?.length) {
          groups.push({ account: apiAccess, keys: filteredKeys })
        }
      })
    }

    // DataTable leaves ordering to the caller once totalCount is set.
    return sortApiAccessRows(groups, sortOrderBy, sortOrder)
  }, [apiAccessList, apiKeyList, searchText, sortOrderBy, sortOrder])

  const onOkDeleteItems = () => {
    const apiAccess = itemsToBeDeleted.filter(
      item => item && !item.key
    ) as IAPIAccess[]
    const apiAccessIds = apiAccess.map(item => item.accountId)
    const apiKeys = itemsToBeDeleted.filter(
      item => item && item.key && !apiAccessIds.includes(item.accountId)
    ) as IAPIKey[]

    if (apiAccess.length > 0) {
      dispatch(deleteAPIAccess(apiAccess, effectiveId))
    }
    if (apiKeys.length > 0) {
      dispatch(deleteAPIKeys(apiKeys, effectiveId))
    }
    setSelectedRows([])
    setOpenDeleteConfirmation(false)
  }

  const onCancelDeleteItems = () => {
    setOpenDeleteConfirmation(false)
  }

  const onOkLatestAddedApiKey = () => {
    dispatch(setLastAddedAPIKey({ apiKey: null }))
  }

  const handleCopyApiKey = () => {
    if (latestAddedApiKey?.key) {
      copy(latestAddedApiKey.key)
    }
  }

  const getIdForField = (item: IAPIAccess & IAPIKey) => {
    if (item && item.key) {
      return item.publicId
    }
    return item.accountId
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    localStorage.setItem('api-access-rowsPerPage', String(newRowsPerPage))
    setRowsPerPage(newRowsPerPage)
    setPage(0)
  }

  useEffect(() => {
    if (!loadingApiAccess && sorterdAPIAccesList.length > 0 && page > 0) {
      const totalPages = Math.ceil(sorterdAPIAccesList.length / rowsPerPage)
      if (page >= totalPages) setPage(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorterdAPIAccesList, loadingApiAccess])

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage
    const endIndex = startIndex + rowsPerPage
    return sorterdAPIAccesList.slice(startIndex, endIndex)
  }, [sorterdAPIAccesList, page, rowsPerPage])

  // Form bodies shared by the slide-out panel (route context) and the nested
  // dialog (embedded in FolderAccessDialog) presentations below.
  const keyEditorTitle = currentEditingKey.accountId
    ? intl.formatMessage({ id: 'screens.apiAccess.updateApiKey' })
    : intl.formatMessage({ id: 'screens.apiAccess.createApiKey' })
  const keyForm = (
    <APIKeyForm
      initialValues={currentEditingKey}
      handleSubmit={(data: IAPIKey) => {
        if (currentEditingKey.accountId) {
          dispatch(updateAPIKey(data, currentEditingApiAccess, effectiveId))
        } else {
          dispatch(createAPIKey(data, currentEditingApiAccess, effectiveId))
        }
        setOpenKeyEditor(false)
      }}
    />
  )

  const accountEditorTitle = currentEditingApiAccess.accountId
    ? intl.formatMessage({ id: 'screens.apiAccess.updateApiAccount' })
    : intl.formatMessage({ id: 'screens.apiAccess.createApiAccount' })
  const accountForm = (
    <APIAccessForm
      initialValues={currentEditingApiAccess}
      handleSubmit={(data: IAPIAccess) => {
        if (currentEditingApiAccess.accountId) {
          dispatch(updateAPIAccess(data, effectiveId))
        } else {
          dispatch(createAPIAccess(data, effectiveId))
        }
        setOpenAPIAccessEditor(false)
      }}
    />
  )

  return (
    <>
      {!hideNav && <UsersNav />}
      {latestAddedApiKey && (
        <ConfirmationDialog
          open={latestAddedApiKey !== null}
          ok={intl.formatMessage(messages.okay)}
          hideCancelButton={true}
          onOk={onOkLatestAddedApiKey}
          onCancel={onOkLatestAddedApiKey}
        >
          <StyledDialogContent>
            <Typography variant="h3" sx={{ fontWeight: 'bold', pb: 1, pt: 1 }}>
              {intl.formatMessage(localMessages.keyGeneratedTitle)}
            </Typography>
            <Box mt={2}>
              <Typography variant="body2">
                {intl.formatMessage(localMessages.keyGeneratedMessage)}
              </Typography>
            </Box>
            <Box mt={2}>
              <Input
                type="text"
                value={latestAddedApiKey.key}
                inputProps={{ readOnly: true }}
                fullWidth={true}
                endAdornment={
                  <InputAdornment position="end">
                    <Button
                      aria-label="toggle password visibility"
                      onClick={handleCopyApiKey}
                    >
                      <Duplicate />
                    </Button>
                  </InputAdornment>
                }
              />
            </Box>
          </StyledDialogContent>
        </ConfirmationDialog>
      )}
      <ConfirmationDialog
        open={openDeleteConfirmation}
        title={intl.formatMessage(localMessages.confirmationDialogTitle, {
          itemCount: itemsToBeDeleted.length
        })}
        message={intl.formatMessage(localMessages.confirmationDialogMessage, {
          itemCount: itemsToBeDeleted.length
        })}
        ok={intl.formatMessage(messages.yesDelete)}
        onOk={onOkDeleteItems}
        onCancel={onCancelDeleteItems}
      />
      <StyledTableTopHeader ref={measuredRef}>
        <Typography variant={'h4'}>
          {intl.formatMessage(localMessages.title)}
        </Typography>
        {projectIdProp && (
          <Typography variant="body2" color="textSecondary" mt={1}>
            {intl.formatMessage(localMessages.folderDescription)}
          </Typography>
        )}
        <StyledTableTopActionButtons>
          <StyledLeftActionButtons>
            <SearchInput
              id="api-access-search-input"
              placeholder={intl.formatMessage(localMessages.searchPlaceholder)}
              autoFocus
              text={searchText}
              onTextChange={text => {
                setSearchText(text)
                setPage(0)
              }}
            />

            <RefreshButton onClick={onRefresh} />

            {selectedRows.length > 0 && (
              <DeleteButton onClick={onDeleteItems} />
            )}
          </StyledLeftActionButtons>
          <StyledRightActionButtons>
            <IconButton
              messageId="screens.apiAccess.createApiAccount"
              onClick={() => {
                setCurrentEditingApiAccess({ name: '' })
                setOpenAPIAccessEditor(true)
              }}
              size="large"
            />
          </StyledRightActionButtons>
        </StyledTableTopActionButtons>
      </StyledTableTopHeader>
      <DataTable
        actions={[
          {
            name: 'Edit',
            callBack: onColumnClick,
            render: Renders.renderEdit,
            disableGutters: true
          },
          {
            name: 'Delete',
            callBack: onDelete,
            render: Renders.renderDelete,
            disableGutters: true
          }
        ]}
        loading={loadingApiAccess}
        columns={[
          {
            field: 'name',
            label: intl.formatMessage(messages.name),
            render: projectNameRender,
            className: rowClassName
          },
          {
            field: 'key',
            label: intl.formatMessage(localMessages.key),
            render: projectKeyRender,
            className: rowClassName
          },
          {
            field: 'expirationDate',
            label: intl.formatMessage(localMessages.expirationDate),
            render: projectExpirationDateRender,
            className: rowClassName
          },
          {
            field: 'role',
            label: (
              <Tooltip
                title="The site-level role may differ from the role assigned at the folder level"
                placement="top"
              >
                <span
                  className={
                    'MuiButtonBase-root MuiTableSortLabel-root MuiTableSortLabel-active'
                  }
                >
                  {intl.formatMessage(messages.userRole)}
                </span>
              </Tooltip>
            ),
            render: roleRender
          }
        ]}
        idField={getIdForField}
        data={paginatedData}
        onColumnClick={disableRowClick ? undefined : onColumnClick}
        onHandleRequestSort={onHandleRequestSort}
        onSelectionChange={setSelectedRows}
        selectedRows={selectedRows}
        headerColor={'#fff'}
        isSelected={(item: IAPIAccess & IAPIKey) => {
          if (item && item.key) {
            return Boolean(
              selectedRows.find(row => {
                if (row && row.key) {
                  return row.publicId === item.publicId
                }
                return row.accountId === item.accountId
              })
            )
          } else {
            const id = getIdForField(item)
            return Boolean(selectedRows.find(row => getIdForField(row) === id))
          }
        }}
        topOffset={0}
        totalCount={sorterdAPIAccesList.length}
        page={page}
        _rowsPerPage={rowsPerPage}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handleRowsPerPageChange}
        _order={sortOrder}
        _orderBy={sortOrderBy}
      />
      {formsInDialog ? (
        <Dialog
          open={openKeyEditor}
          onClose={() => setOpenKeyEditor(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2
            }}
          >
            {keyEditorTitle}
            <MuiIconButton
              aria-label="Close"
              size="small"
              onClick={() => setOpenKeyEditor(false)}
            >
              <Close />
            </MuiIconButton>
          </DialogTitle>
          <MuiDialogContent>{keyForm}</MuiDialogContent>
        </Dialog>
      ) : (
        <MikeSlidingPanel
          position="right"
          isOpen={openKeyEditor}
          onClose={() => {
            setOpenKeyEditor(false)
          }}
          titleArea={keyEditorTitle}
          contentArea={keyForm}
          actionsArea={null}
          noGrayOverlay={false}
        />
      )}
      {formsInDialog ? (
        <Dialog
          open={openAPIAccessEditor}
          onClose={() => setOpenAPIAccessEditor(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2
            }}
          >
            {accountEditorTitle}
            <MuiIconButton
              aria-label="Close"
              size="small"
              onClick={() => setOpenAPIAccessEditor(false)}
            >
              <Close />
            </MuiIconButton>
          </DialogTitle>
          <MuiDialogContent>{accountForm}</MuiDialogContent>
        </Dialog>
      ) : (
        <MikeSlidingPanel
          position="right"
          isOpen={openAPIAccessEditor}
          onClose={() => {
            setOpenAPIAccessEditor(false)
          }}
          titleArea={accountEditorTitle}
          contentArea={accountForm}
          actionsArea={null}
          noGrayOverlay={false}
        />
      )}
    </>
  )
}

export default APIAccess
